import {
  DEFAULT_QUERY_VALUES,
  MAX_LENGTH_FOR_SEARCH_INPUT,
  QUERY_KEY,
  SORTING_METHODS,
} from '~~/constants/typesense';
import { CT_SEARCH_REFINEMENT_LIST_FILTER_NAMES } from '~~/constants/commercetoolsProductSearch';
import type {
  ProductListingFilterState,
  ProductListingRefinementList,
} from '~~/types/productListing/ProductListingFilterState';

// Uses the same query keys as the Typesense routing so existing URLs keep working.
// `defaultFilters` (CMS preFilter) fill the refinement lists the URL leaves out. A removed default
// is written as an empty `refinementList[<attribute>]`, otherwise it would come straight back.

const DEFAULT_SORT_BY = SORTING_METHODS[0]?.value ?? '';
const DEFAULT_PAGE = DEFAULT_QUERY_VALUES[QUERY_KEY.PAGE];
const DEFAULT_HITS_PER_PAGE = DEFAULT_QUERY_VALUES[QUERY_KEY.HITS_PER_PAGE];

export const DEFAULT_PRODUCT_LISTING_FILTER_STATE: ProductListingFilterState = {
  page: DEFAULT_PAGE,
  hitsPerPage: DEFAULT_HITS_PER_PAGE,
  sortBy: DEFAULT_SORT_BY,
  query: '',
  refinementList: {},
  range: {},
  ratingMenu: {},
};

const getClearedRefinementListKey = (attribute: string) => `refinementList[${attribute}]`;

const isSortingMethod = (value: string): boolean => SORTING_METHODS.some(method => method.value === value);

const toSingleValue = (value: unknown): string | undefined => {

  if (Array.isArray(value)) {

    return typeof value[0] === 'string' ? value[0] : undefined;

  }

  return typeof value === 'string' ? value : undefined;

};

export const queryToProductListingState = (
  query: Record<string, unknown>,
  defaultFilters: ProductListingRefinementList = {},
): ProductListingFilterState => {

  const state: ProductListingFilterState = {
    page: DEFAULT_PAGE,
    hitsPerPage: DEFAULT_HITS_PER_PAGE,
    sortBy: DEFAULT_SORT_BY,
    query: '',
    refinementList: {},
    range: {},
    ratingMenu: {},
  };

  const pageValue = toSingleValue(query.page);

  if (pageValue && Number.isFinite(Number(pageValue))) {

    state.page = Number(pageValue);

  }

  const hitsPerPageValue = toSingleValue(query.hitsPerPage);

  if (hitsPerPageValue && Number.isFinite(Number(hitsPerPageValue))) {

    state.hitsPerPage = Number(hitsPerPageValue);

  }

  const sortByValue = toSingleValue(query.sortBy);

  if (sortByValue !== undefined && isSortingMethod(sortByValue)) {

    state.sortBy = sortByValue;

  }

  const queryValue = toSingleValue(query.query);

  if (queryValue !== undefined) {

    state.query = queryValue.slice(0, MAX_LENGTH_FOR_SEARCH_INPUT);

  }

  CT_SEARCH_REFINEMENT_LIST_FILTER_NAMES.forEach((attribute) => {

    const values: string[] = [];
    let index = 0;

    while (query[`refinementList[${attribute}][${index}]`] !== undefined) {

      const value = toSingleValue(query[`refinementList[${attribute}][${index}]`]);

      if (value) {

        values.push(value);

      }
      index += 1;

    }

    const defaultValues = defaultFilters[attribute];

    if (values.length) {

      state.refinementList[attribute] = values;

    } else if (defaultValues?.length && query[getClearedRefinementListKey(attribute)] === undefined) {

      state.refinementList[attribute] = defaultValues;

    }

  });

  const rawPriceRange = toSingleValue(query['range[price]']);

  if (rawPriceRange) {

    const [min, max] = rawPriceRange.split(':');
    const priceRange: { min?: number; max?: number } = {};

    if (min !== '' && Number.isFinite(Number(min))) {

      priceRange.min = Number(min);

    }

    if (max !== '' && Number.isFinite(Number(max))) {

      priceRange.max = Number(max);

    }

    if (priceRange.min !== undefined || priceRange.max !== undefined) {

      state.range.price = priceRange;

    }

  }

  const ratingValue = toSingleValue(query['ratingMenu[avgRating]']);

  if (ratingValue && Number.isFinite(Number(ratingValue))) {

    state.ratingMenu.avgRating = Number(ratingValue);

  }

  const categoryValue = toSingleValue(query.category);

  if (categoryValue) {

    state.category = categoryValue;

  }

  return state;

};

export const productListingStateToQuery = (
  state: ProductListingFilterState,
  defaultFilters: ProductListingRefinementList = {},
): Record<string, string> => {

  const query: Record<string, string> = {};

  if (state.query) {

    query.query = state.query;

  }

  CT_SEARCH_REFINEMENT_LIST_FILTER_NAMES.forEach((attribute) => {

    const values = state.refinementList[attribute] || [];

    values.forEach((value, index) => {

      query[`refinementList[${attribute}][${index}]`] = value;

    });

    if (!values.length && defaultFilters[attribute]?.length) {

      query[getClearedRefinementListKey(attribute)] = '';

    }

  });

  const priceRange = state.range.price;

  if (priceRange && (priceRange.min !== undefined || priceRange.max !== undefined)) {

    query['range[price]'] = `${priceRange.min ?? ''}:${priceRange.max ?? ''}`;

  }

  if (state.ratingMenu.avgRating !== undefined) {

    query['ratingMenu[avgRating]'] = String(state.ratingMenu.avgRating);

  }

  if (state.category) {

    query.category = state.category;

  }

  if (state.sortBy && state.sortBy !== DEFAULT_SORT_BY) {

    query.sortBy = state.sortBy;

  }

  if (state.hitsPerPage !== DEFAULT_HITS_PER_PAGE) {

    query.hitsPerPage = String(state.hitsPerPage);

  }

  if (state.page !== DEFAULT_PAGE) {

    query.page = String(state.page);

  }

  return query;

};
